import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/constants/routes';
import { useTimezone } from '@/context/timezone-context';
import { useBlogPosts } from '@/hooks/use-blog-posts';
import { cn } from '@/lib/tw-merge/cn';
import {
  BlogCardSkeleton,
  NewsCard,
} from '@/pages/site/blog-page/components/news-card';
import type { BlogPost, BlogVariant } from '@/types/blog';
import { BLOG_TENANT_ID } from '@/utils/blog-config';
import { mapBlogPostToNewsItem } from '@/utils/blog-mappers';

const LATEST_NEWS_LIMIT = 3;
const BLOG_VARIANTS: BlogVariant[] = ['ledger', 'inference'];

interface LatestNewsProps {
  variant?: BlogVariant;
}

const getPostSortTime = (post: BlogPost) => {
  const dateValue = post.date || post.modified;
  if (!dateValue) return 0;

  const parsed = new Date(dateValue).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export default function LatestNews({ variant }: LatestNewsProps) {
  const { formatUserTimestamp } = useTimezone();
  const ledgerPosts = useBlogPosts({
    variant: 'ledger',
    page: 1,
    perPage: LATEST_NEWS_LIMIT,
    status: 'publish',
  });
  const inferencePosts = useBlogPosts({
    variant: 'inference',
    page: 1,
    perPage: LATEST_NEWS_LIMIT,
    status: 'publish',
  });

  const news = useMemo(
    () =>
      (variant ? [variant] : BLOG_VARIANTS)
        .flatMap(blogVariant => {
          const posts =
            blogVariant === 'ledger'
              ? ledgerPosts.data?.posts
              : inferencePosts.data?.posts;

          return (posts ?? []).map(post => ({ post, variant: blogVariant }));
        })
        .sort((a, b) => getPostSortTime(b.post) - getPostSortTime(a.post))
        .slice(0, LATEST_NEWS_LIMIT)
        .map(({ post, variant }) => {
          const item = mapBlogPostToNewsItem(post, variant);
          return {
            ...item,
            timestamp: post.date ? formatUserTimestamp(post.date, 'blog') : '',
          };
        }),
    [
      formatUserTimestamp,
      inferencePosts.data?.posts,
      ledgerPosts.data?.posts,
      variant,
    ]
  );
  const hasBlogConfig = Boolean(BLOG_TENANT_ID);
  const isLoading =
    hasBlogConfig &&
    (variant === 'ledger'
      ? ledgerPosts.isInitialLoading
      : variant === 'inference'
        ? inferencePosts.isInitialLoading
        : ledgerPosts.isInitialLoading || inferencePosts.isInitialLoading) &&
    news.length === 0;
  const blogRoute =
    variant === 'ledger'
      ? ROUTES.BLOG_LEDGER
      : variant === 'inference'
        ? ROUTES.BLOG_INFERENCE
        : ROUTES.BLOG;
  const blogLinkLabel =
    variant === 'ledger'
      ? 'See more in The Ledger'
      : variant === 'inference'
        ? 'See more in The Inference'
        : 'See more on the blog';

  if (!isLoading && news.length === 0) return null;

  return (
    <section className="app-container bg-white py-8 sm:py-12 md:py-16 lg:py-24">
      <div
        className={cn(
          'mb-6 flex flex-col items-start justify-between gap-2 sm:mb-12 md:flex-row md:items-end',
          variant === 'ledger' &&
            'items-center text-center sm:items-start sm:text-left'
        )}
      >
        <h2
          className={cn(
            'font-fraunces text-2xl font-bold leading-[32px] text-foreground sm:text-[40px] sm:leading-[48px]',
            variant === 'ledger' && 'leading-[28px]'
          )}
        >
          The latest news
        </h2>
        <Link
          to={blogRoute}
          className="flex items-center gap-2 font-medium text-gray-800 transition-colors hover:opacity-70 sm:text-base"
        >
          {blogLinkLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {isLoading
          ? Array.from({ length: LATEST_NEWS_LIMIT }).map((_, index) => (
              <BlogCardSkeleton key={`latest-news-skeleton-${index}`} />
            ))
          : news.map(item => (
              <NewsCard key={item.href ?? item.title} item={item} />
            ))}
      </div>
    </section>
  );
}
